import { Image as HeroUiImage, type ImageProps } from '@heroui/image';
import { forwardRef } from 'react';

/**
 * HeroUI Image derives `data-loaded` and skeleton opacity from preload state that can
 * diverge between SSR and the client's first paint (e.g. cached images). Suppress the
 * known attribute mismatch on `<img>` and default-disable skeleton so markup stays stable.
 *
 * It tracks that state by loading the src a second time through a detached `new Image()`
 * probe and mirrors our `loading` prop onto it. With `loading="lazy"` the probe never
 * intersects the viewport, so Chrome defers it indefinitely: `data-loaded` stays false and
 * the real `<img>` is stuck at `opacity-0`. It only appears after a reload, when the probe
 * resolves synchronously from the HTTP cache. Passing `as` opts into HeroUI's
 * `shouldBypassImageLoad` branch, which skips the probe entirely while rendering the same
 * `<img>` with the same classes — so native lazy loading is preserved without the probe.
 * Keep the probe when a caller needs its load/error events (`fallbackSrc`, `onLoad`,
 * `onError`), since those only fire from it.
 */
export const Image = forwardRef<HTMLImageElement, ImageProps>(
  ({ disableSkeleton = true, as, ...props }, ref) => {
    const needsLoadProbe = Boolean(props.fallbackSrc || props.onLoad || props.onError);

    return (
      <HeroUiImage
        ref={ref}
        as={as ?? (needsLoadProbe ? undefined : 'img')}
        {...props}
        disableSkeleton={disableSkeleton}
        suppressHydrationWarning
      />
    );
  },
);
Image.displayName = 'Image';
